import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  MainContainer, StyledCard, FormGrid, StyledFormGroup,
  CardTitle, TooltipWrapper, InfoIcon,
  DropdownList, DropdownItem, DashButton, NcmIpiChip,
  NcmHeader,
  ConflitoNaturezaBanner, ConflitoNaturezaLink, AvisoCampo, AvisoCampoAcao
} from './style';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { ScenarioCard } from './ScenarioCard';
import { EnquadramentoIpiOption, NcmProfileDto, NcmProfileItemDto } from '../NcmRules/types';
import NcmNaturezaCampo, { NcmConflito } from './NcmNaturezaCampo';
import { TAX_CODES_VAZIO, TaxCodesData, icmsPorAliquota, normalizarTaxCodes } from '../taxCodes';

export type { TaxCodeOption, TaxCodesData } from '../taxCodes';
export interface ClassTribOption { code: string; description: string; cstIbsCbsId: number | null }

interface ConflitoNatureza {
  taxConfigId: number;
  nome: string | null;
}

interface TaxConfigUnicidadeConflitoError {
  code: 'NCM_JA_EM_OUTRA_NATUREZA' | 'NBS_JA_EM_OUTRA_NATUREZA';
  message: string;
  conflitos: Array<{ taxConfigId: number; nome: string | null }>;
}

interface TaxConfigGenericErrorResponse {
  code?: string;
  message?: string | string[];
}

type TaxConfigErrorResponse =
  | TaxConfigUnicidadeConflitoError
  | TaxConfigGenericErrorResponse;

interface LayoutContext {
  setHandleChildSubmit: (fn: () => void) => void;
}

interface Company {
  id: number;
  companyName: string;
  cnpj: string;
  stateId: number;
  tipoEstabelecimento?: string;
  state: {
    id: number;
    name: string;
  };
}

export interface TaxScenario {
  id: number;
  cstICMS: string;
  aliquotaICMS: number | string;
  baseDeCalculo: number | string;
  cstIBS_CBS: string;
  classificacaoTributaria: string;
  aliquotaCBS: number;
  aliquotaIBS: number;
  diferencialAliquota: boolean;
  tipoDiferencial?: string;
  stateIds: number[];
  tempStateNames?: string;
  regions: string[];
  showStateInput: boolean;
  applyToSameState: boolean;
}

export const DEFAULT_SCENARIO: Omit<TaxScenario, 'id'> = {
  aliquotaICMS: '',
  aliquotaCBS: 0,
  aliquotaIBS: 0,
  baseDeCalculo: '',
  cstICMS: '',
  cstIBS_CBS: '',
  classificacaoTributaria: '',
  diferencialAliquota: false,
  tipoDiferencial: '',
  stateIds: [],
  regions: [],
  showStateInput: false,
  tempStateNames: '',
  applyToSameState: false,
};

const LabelWithInfo = ({ label, tooltip }: { label: string, tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

const CreateTaxScenario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = new URLSearchParams(location.search).get('editId');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [selectedNcms, setSelectedNcms] = useState<{ id: number; name: string }[]>([]);
  const [cstPIS_COFINS, setCstPIS_COFINS] = useState('01');
  const [aliquotaPIS, setAliquotaPIS] = useState<number | string>('');
  const [aliquotaCOFINS, setAliquotaCOFINS] = useState<number | string>('');
  const [cstIPI, setCstIPI] = useState('');
  // Vazio = padrão pelo CST na emissão (999 para os CST sem isenção, imunidade ou suspensão).
  const [codigoEnquadramentoIPI, setCodigoEnquadramentoIPI] = useState('');
  const [enqOptions, setEnqOptions] = useState<EnquadramentoIpiOption[]>([]);
  const [scenarioName, setScenarioName] = useState('');
  const { setHandleChildSubmit } = useOutletContext<LayoutContext>();
  const [cfops, setCfops] = useState<{ id: number; code: string; description: string }[]>([]);
  const [selectedCfop, setSelectedCfop] = useState<number | string>('');
  const [isLoadingCfops, setIsLoadingCfops] = useState(false);
  const [cfopSearch, setCfopSearch] = useState("");
  const [isCfopListOpen, setIsCfopListOpen] = useState(false);
  const { setFeedback } = useContext(FeedbackContext);
  const [allStates, setAllStates] = useState<{ id: number; name: string; uf: string }[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCodesData>(TAX_CODES_VAZIO);
  const [classTribOptions, setClassTribOptions] = useState<ClassTribOption[]>([]);
  const [conflitoNatureza, setConflitoNatureza] = useState<ConflitoNatureza | null>(null);
  const [taxCodesStatus, setTaxCodesStatus] = useState<'carregando' | 'ok' | 'erro'>('carregando');

  const [scenarios, setScenarios] = useState<TaxScenario[]>([
    { id: Date.now(), ...DEFAULT_SCENARIO }
  ]);

  const updateScenario = (id: number, field: string, value: any) => {
    setScenarios(prev => prev.map(s =>
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const fetchCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    try {
      const response = await ApiService.fiscal.getCompanyGateways('cenario');
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      const mapped = (data || []).map((cg: any) => {
        const state = cg.Company?.Address?.Neighborhood?.City?.State ?? null;
        return {
          id: cg.companyId,
          cnpj: cg.Company?.cnpj,
          companyName: cg.Company?.companyName,
          tipoEstabelecimento: cg.Company?.tipoEstabelecimento,
          state: state ? { id: state.id, name: state.name } : null,
        };
      });
      setCompanies(mapped);
    } catch (error) {
      console.error("Erro ao buscar empresas emissoras:", error);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);

  const [ncmProfilesByNcmId, setNcmProfilesByNcmId] = useState<Record<number, NcmProfileDto[]>>({});
  const [itensEmpresa, setItensEmpresa] = useState<NcmProfileItemDto[]>([]);
  const [naturezasPorNcm, setNaturezasPorNcm] = useState<Record<number, NcmConflito>>({});

  const fetchNcmProfiles = useCallback(async () => {
    try {
      const { data } = await ApiService.fiscal.listNcmProfiles();
      const map: Record<number, NcmProfileDto[]> = {};
      (data?.itens || []).forEach((item) => { map[item.ncmId] = item.perfis; });
      setNcmProfilesByNcmId(map);
      setItensEmpresa(data?.itens || []);
    } catch (error) {
      console.error("Erro ao buscar regras por NCM:", error);
    }
  }, []);

  useEffect(() => {
    let ativo = true;
    if (!cstIPI) {
      setEnqOptions([]);
      return;
    }
    ApiService.fiscal
      .getEnquadramentosIpi(cstIPI)
      .then((r) => {
        if (!ativo) return;
        const lista = r.data ?? [];
        setEnqOptions(lista);
        setCodigoEnquadramentoIPI((atual) => (atual && !lista.some((o) => o.code === atual) ? '' : atual));
      })
      .catch((error: unknown) => console.error('Erro ao buscar enquadramentos do IPI:', error));
    return () => {
      ativo = false;
    };
  }, [cstIPI]);

  useEffect(() => {
    if (!selectedCfop || !selectedCompany) {
      setNaturezasPorNcm({});
      return;
    }
    let ativo = true;
    ApiService.fiscal
      .listNcmNaturezas({ cfopId: Number(selectedCfop), companyEmissorId: Number(selectedCompany) })
      .then((r) => {
        if (!ativo) return;
        const mapa: Record<number, NcmConflito> = {};
        (r.data ?? []).forEach((n) => {
          if (String(n.taxConfigId) !== editId) mapa[n.ncmId] = { taxConfigId: n.taxConfigId, nome: n.nome };
        });
        setNaturezasPorNcm(mapa);
      })
      .catch((error: unknown) => {
        if (ativo) setNaturezasPorNcm({});
        console.error('Erro ao buscar naturezas dos NCMs:', error);
      });
    return () => {
      ativo = false;
    };
  }, [selectedCfop, selectedCompany, editId]);

  useEffect(() => {
    fetchCompanies();
    fetchNcmProfiles();
  }, [fetchCompanies, fetchNcmProfiles]);

  const addNcm = (opcao: { id: number; name: string }) => {
    setSelectedNcms((atual) => (atual.some((s) => s.id === opcao.id) ? atual : [...atual, { id: opcao.id, name: opcao.name }]));
  };

  const removeNcm = (id: number) => {
    setSelectedNcms((atual) => atual.filter(item => item.id !== id));
  };

  const fetchCfops = useCallback(async () => {
    setIsLoadingCfops(true);
    try {
      const response = await ApiService.fiscal.getCfops();
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setCfops(data || []);
    } catch (error) {
      console.error("Erro ao buscar CFOPs:", error);
    } finally {
      setIsLoadingCfops(false);
    }
  }, []);

  useEffect(() => {
    fetchCfops();
  }, [fetchCfops]);

  // Pré-carga para modo de edição
  useEffect(() => {
    if (!editId) return;
    const loadExisting = async () => {
      try {
        const { data } = await ApiService.fiscal.getTaxConfig(Number(editId));
        setScenarioName(data.nome || '');
        setSelectedCompany(String(data.companyEmissorId || ''));
        setSelectedCfop(data.cfopId || '');
        setCstPIS_COFINS(data.cstPIS_COFINS || '01');
        setAliquotaPIS(data.aliquotaPIS !== undefined && data.aliquotaPIS !== null ? Number(data.aliquotaPIS) : '');
        setAliquotaCOFINS(data.aliquotaCOFINS !== undefined && data.aliquotaCOFINS !== null ? Number(data.aliquotaCOFINS) : '');
        setCstIPI(data.cstIPI || '');
        // Natureza existente: carrega exatamente o que está salvo (mesmo vazio) — "999" é só
        // sugestão de natureza nova (estado inicial), nunca substitui o valor gravado.
        setCodigoEnquadramentoIPI(data.codigoEnquadramentoIPI || '');
        if (Array.isArray(data.TaxClassifier)) {
          const ncms = data.TaxClassifier
            .filter((c: any) => c.Ncm)
            .map((c: any) => ({ id: c.Ncm.id, name: c.Ncm.ncm || c.Ncm.name || String(c.ncmId) }));
          setSelectedNcms(ncms);
        }
        if (Array.isArray(data.TaxScenario) && data.TaxScenario.length > 0) {
          setScenarios(data.TaxScenario.map((s: any) => ({
            id: s.id,
            cstICMS: s.cstICMS || '',
            aliquotaICMS: s.aliquotaICMS !== undefined && s.aliquotaICMS !== null ? Number(s.aliquotaICMS) : '',
            baseDeCalculo: s.baseDeCalculo !== undefined && s.baseDeCalculo !== null ? Number(s.baseDeCalculo) : '',
            cstIBS_CBS: s.cstIBS_CBS || '',
            classificacaoTributaria: s.classificacaoTributaria || '',
            aliquotaCBS: Number(s.aliquotaCBS) || 0,
            aliquotaIBS: Number(s.aliquotaIBS) || 0,
            diferencialAliquota: Boolean(s.diferencialAliquota),
            tipoDiferencial: s.diferencialAliquota ? 'difal' : 'normal',
            stateIds: (s.TaxScenarioState || []).filter((st: any) => st.stateId).map((st: any) => st.stateId),
            regions: (s.TaxScenarioState || []).filter((st: any) => st.region).map((st: any) => st.region),
            showStateInput: false,
            tempStateNames: '',
            applyToSameState: false,
          })));
        }
      } catch (error) {
        console.error('Erro ao carregar cenário para edição:', error);
      }
    };
    loadExisting();
  }, [editId]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const { data } = await ApiService.companies.getStates();
        setAllStates(data);
      } catch (error) {
        console.error("Erro ao carregar estados", error);
      }
    };
    loadStates();
  }, []);

  const loadTaxCodes = useCallback(async () => {
    setTaxCodesStatus('carregando');
    try {
      const [codes, classTrib] = await Promise.all([
        ApiService.fiscal.getTaxCodes(),
        ApiService.fiscal.getClassTrib(),
      ]);
      setTaxCodes(normalizarTaxCodes(codes.data));
      setClassTribOptions(Array.isArray(classTrib.data) ? classTrib.data : []);
      setTaxCodesStatus('ok');
    } catch (error) {
      console.error("Erro ao carregar tabelas tributárias", error);
      setTaxCodesStatus('erro');
    }
  }, []);

  useEffect(() => {
    loadTaxCodes();
  }, [loadTaxCodes]);

  const filteredCfops = cfops.filter(opt => {
    const search = cfopSearch.toLowerCase();
    const code = String(opt.code || "").toLowerCase();
    const description = String(opt.description || "").toLowerCase();
    return code.includes(search) || description.includes(search);
  });

  const selectedCfopData = cfops.find(c => Number(c.id) === Number(selectedCfop));

  // IPI herdado do NCM (perfil do emissor selecionado, senão o do tenant) — só leitura;
  // o vendedor não edita IPI aqui (perfil por NCM cadastra-se em Regras por NCM).
  // "IPI nesta operação": vazio = segue a regra do NCM; só CSTs que não incidem na saída (52–55).
  const cstIpiNaturezaOpcoes = taxCodes.cstIpi.filter((c) => c.naoIncideNaNatureza);
  const cstIpiNatureza = cstIpiNaturezaOpcoes.find((c) => c.code === cstIPI);
  const ipiNaoIncide = Boolean(cstIpiNatureza);
  const cstIpiGravadoInvalido = taxCodesStatus === 'ok' && cstIPI !== '' && !cstIpiNatureza;
  const resolveIpiChip = (ncmId: number): { tone: 'ok' | 'mute' | 'bad'; label: string; clickable: boolean } => {
    if (ipiNaoIncide) {
      return { tone: 'mute', label: `IPI ${cstIPI} · não incide`, clickable: false };
    }
    const perfis = ncmProfilesByNcmId[ncmId] || [];
    const porEmissor = selectedCompany
      ? perfis.find(p => p.companyEmissorId === Number(selectedCompany))
      : undefined;
    const perfil = porEmissor || perfis.find(p => p.companyEmissorId === null);
    if (!perfil || !perfil.cstIPI) {
      return { tone: 'bad', label: 'sem regra', clickable: true };
    }
    return { tone: 'ok', label: `IPI ${perfil.cstIPI} · ${perfil.aliquotaIPI ?? '0'}%`, clickable: false };
  };

  const handleSubmit = useCallback(async () => {
    setConflitoNatureza(null);
    try {
      if (taxCodesStatus !== 'ok') {
        return setFeedback({
          isOpen: true,
          message: taxCodesStatus === 'erro'
            ? "Não foi possível carregar as tabelas tributárias. Use \"Tentar de novo\" antes de salvar."
            : "Aguarde o carregamento das tabelas tributárias para salvar.",
          statusOk: false,
        });
      }

      if (!selectedCompany) {
        return setFeedback({ isOpen: true, message: "Selecione uma empresa antes de salvar.", statusOk: false });
      }

      if (!selectedCfop) {
        return setFeedback({ isOpen: true, message: "O campo CFOP é obrigatório.", statusOk: false });
      }

      if (selectedNcms.length === 0) {
        return setFeedback({ isOpen: true, message: "Adicione pelo menos um NCM ao cenário.", statusOk: false });
      }

      if (cstIpiNatureza && cstIpiNatureza.enquadramentoPadrao === null && !codigoEnquadramentoIPI) {
        return setFeedback({ isOpen: true, message: `Escolha o enquadramento (cEnq) do CST IPI ${cstIpiNatureza.code}.`, statusOk: false });
      }

      const currentCompany = companies.find(c => Number(c.id) === Number(selectedCompany));
      const originStateId = currentCompany?.state?.id || currentCompany?.stateId;

      const payload = {
        nome: scenarioName,
        cfopId: Number(selectedCfop),
        companyEmissorId: Number(selectedCompany),
        cstIPI: cstIpiNatureza ? cstIpiNatureza.code : null,
        codigoEnquadramentoIPI: cstIpiNatureza ? codigoEnquadramentoIPI || null : null,
        cstPIS_COFINS: cstPIS_COFINS,
        aliquotaPIS: String(Number(aliquotaPIS || 0).toFixed(2)),
        aliquotaCOFINS: String(Number(aliquotaCOFINS || 0).toFixed(2)),
        ncmIds: selectedNcms.map(n => Number(n.id)),

        scenarios: scenarios.map(s => {
          const finalStateIds = Array.isArray(s.stateIds) ? s.stateIds.map(id => Number(id)) : [];

          if (s.applyToSameState && originStateId) {
            const id = Number(originStateId);
            if (!finalStateIds.includes(id)) {
              finalStateIds.push(id);
            }
          }

          const porAliquota = icmsPorAliquota(taxCodes, s.cstICMS);
          const scenarioObj: any = {
            cstIBS_CBS: s.cstIBS_CBS,
            classificacaoTributaria: s.classificacaoTributaria,
            aliquotaCBS: String(s.aliquotaCBS || "0.00"),
            aliquotaIBS: String(s.aliquotaIBS || "0.00"),
            cstICMS: s.cstICMS,
            aliquotaICMS: String(Number(porAliquota ? s.aliquotaICMS || 0 : 0).toFixed(2)),
            baseDeCalculo: String(Number(porAliquota ? s.baseDeCalculo || 0 : 0).toFixed(2)),
            diferencialAliquota: Boolean(s.diferencialAliquota),
            stateIds: finalStateIds,
          };

          if (s.regions && s.regions.length > 0) {
            scenarioObj.regions = s.regions;
          }

          return scenarioObj;
        })
      };

      if (editId) {
        const { ncmIds, ...updatePayload } = payload;
        await ApiService.fiscal.updateTaxConfig(Number(editId), {
          ...updatePayload,
          classifierIds: ncmIds,
        });
      } else {
        await ApiService.fiscal.createTaxConfig(payload);
      }

      setFeedback({ isOpen: true, message: `Cenário fiscal ${editId ? 'atualizado' : 'salvo'} com sucesso!`, statusOk: true });

      setTimeout(() => navigate('/panel/invoice/tax-scenario'), 2000);

    } catch (error: unknown) {
      if (!isAxiosError<TaxConfigErrorResponse>(error)) {
        console.error("ERRO COMPLETO:", error);
        setFeedback({ isOpen: true, message: "Erro ao salvar: Erro interno no servidor.", statusOk: false });
        return;
      }

      const data = error.response?.data;
      console.error("ERRO COMPLETO:", data);

      const ehConflitoDeUnicidade =
        data?.code === 'NCM_JA_EM_OUTRA_NATUREZA' || data?.code === 'NBS_JA_EM_OUTRA_NATUREZA';

      if (data && ehConflitoDeUnicidade) {
        const conflito = 'conflitos' in data ? data.conflitos[0] : undefined;
        setConflitoNatureza(
          conflito?.taxConfigId != null
            ? { taxConfigId: conflito.taxConfigId, nome: conflito.nome }
            : null,
        );
        setFeedback({
          isOpen: true,
          message: data.message ? String(data.message) : 'NCM já está configurado em outra natureza.',
          statusOk: false
        });
        return;
      }

      const msg = data?.message;
      setFeedback({ isOpen: true, message: `Erro ao salvar: ${Array.isArray(msg) ? msg.join(", ") : msg || "Erro interno no servidor."}`, statusOk: false });
    }
  }, [scenarioName, selectedCfop, selectedCompany, companies, selectedNcms, scenarios, cstIPI, cstIpiNatureza, taxCodes, taxCodesStatus, cstPIS_COFINS, codigoEnquadramentoIPI, aliquotaPIS, aliquotaCOFINS, navigate, editId]);

  useEffect(() => {
    if (setHandleChildSubmit) {
      setHandleChildSubmit(() => handleSubmit);
    }
  }, [handleSubmit, setHandleChildSubmit]);

  return (
    <>
      <MainContainer style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        <StyledCard>
          <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Configuração Base</CardTitle>
          <FormGrid cols={2}>
            <StyledFormGroup>
              <LabelWithInfo label="Nome do cenário" tooltip="Nome interno para identificar este cenário" />
              <input
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="Digite o nome do cenário..."
              />
            </StyledFormGroup>

            <StyledFormGroup>
              <LabelWithInfo label="CNPJ de origem" tooltip="CNPJ da sua empresa que emitirá a nota" />
              <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} disabled={isLoadingCompanies}>
                <option value="">{isLoadingCompanies ? 'Carregando empresas...' : 'Selecione uma empresa'}</option>
                {companies.map((company) => {
                  const tipo = company.tipoEstabelecimento || (company.cnpj ? (company.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
                  return (
                    <option key={company.id} value={company.id}>
                      {company.cnpj} — {tipo ? `${tipo} — ` : ''}{company.state?.name ? `${company.state.name} — ` : ''}{company.companyName}
                    </option>
                  );
                })}
              </select>
            </StyledFormGroup>

            <StyledFormGroup style={{ position: 'relative' }}>
              <LabelWithInfo label="CFOP" tooltip="Código Fiscal de Operações e Prestações" />

              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Pesquisar CFOP..."
                  autoComplete="off"
                  value={isCfopListOpen ? cfopSearch : (selectedCfopData ? `${selectedCfopData.code} — ${selectedCfopData.description}` : "")}
                  onChange={(e) => {
                    setCfopSearch(e.target.value);
                    setIsCfopListOpen(true);
                  }}
                  onFocus={() => {
                    setCfopSearch("");
                    setIsCfopListOpen(true);
                  }}
                  onBlur={() => setTimeout(() => setIsCfopListOpen(false), 200)}
                  style={{
                    width: '100%', padding: '12px 15px', borderRadius: '25px',
                    border: '1px solid #e0e0e0', background: '#f0f0f0',
                    fontSize: '14px', outline: 'none'
                  }}
                />

                {isCfopListOpen && filteredCfops.length > 0 && (
                  <DropdownList>
                    {filteredCfops.map((cfop) => (
                      <DropdownItem
                        key={cfop.id}
                        onMouseDown={() => {
                          setSelectedCfop(cfop.id);
                          setIsCfopListOpen(false);
                        }}
                      >
                        <strong>{cfop.code}</strong> — {cfop.description}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </div>
            </StyledFormGroup>

            <StyledFormGroup>
              <NcmHeader>
                <LabelWithInfo
                  label={selectedNcms.length > 0 ? `NCM(s) · ${selectedNcms.length}` : 'NCM(s)'}
                  tooltip="Nomenclatura Comum do Mercosul aplicável"
                />
                {ipiNaoIncide && selectedNcms.length > 0 && (
                  <NcmIpiChip $tone="mute">IPI {cstIPI} · não incide para todos</NcmIpiChip>
                )}
              </NcmHeader>
              <NcmNaturezaCampo
                selecionados={selectedNcms}
                itensEmpresa={itensEmpresa}
                conflitos={naturezasPorNcm}
                ipiChipDe={resolveIpiChip}
                ipiNaoIncide={ipiNaoIncide}
                onAdicionar={addNcm}
                onRemover={removeNcm}
                onAbrirRegras={() => navigate('/panel/invoice/tax-scenario/ncm-rules?semRegra=true')}
              />
            </StyledFormGroup>
          </FormGrid>

          {conflitoNatureza && (
            <ConflitoNaturezaBanner>
              NCM já configurado nesta operação (mesmo CFOP e emissor).{' '}
              <ConflitoNaturezaLink
                type="button"
                onClick={() => navigate(`/panel/invoice/tax-scenario/create?editId=${conflitoNatureza.taxConfigId}`)}
              >
                Abrir &quot;{conflitoNatureza.nome ?? 'natureza conflitante'}&quot;
              </ConflitoNaturezaLink>
            </ConflitoNaturezaBanner>
          )}
        </StyledCard>

        {/* IMPOSTOS BASE */}
        <StyledCard>
          <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Impostos Base</CardTitle>
          <FormGrid cols={1}>
            <StyledFormGroup>
              <LabelWithInfo label="CST PIS/COFINS" tooltip="Código de Situação Tributária do PIS e COFINS" />
              <select value={cstPIS_COFINS} onChange={(e) => setCstPIS_COFINS(e.target.value)}>
                {taxCodes.cstPisCofins.map(opt => (
                  <option key={opt.code} value={opt.code}>{opt.code} — {opt.description}</option>
                ))}
              </select>
            </StyledFormGroup>
          </FormGrid>

          <FormGrid cols={2} style={{ marginTop: '20px' }}>
            <StyledFormGroup>
              <LabelWithInfo label="Alíquota PIS%" tooltip="Percentual de PIS" />
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={aliquotaPIS}
                onChange={(e) => setAliquotaPIS(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </StyledFormGroup>
            <StyledFormGroup>
              <LabelWithInfo label="Alíquota COFINS%" tooltip="Percentual de COFINS" />
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={aliquotaCOFINS}
                onChange={(e) => setAliquotaCOFINS(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </StyledFormGroup>
          </FormGrid>

          <FormGrid cols={1} style={{ marginTop: '20px' }}>
            <StyledFormGroup>
              <LabelWithInfo label="IPI nesta operação" tooltip="Vazio: cada NCM usa a própria regra (Regras por NCM). Um CST aqui zera o IPI de todos os NCMs desta natureza." />
              <select
                value={cstIpiNatureza ? cstIpiNatureza.code : ''}
                onChange={(e) => {
                  setCstIPI(e.target.value);
                  setCodigoEnquadramentoIPI('');
                }}
              >
                <option value="">Segue a regra do NCM</option>
                {cstIpiNaturezaOpcoes.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.code} · {opt.description}</option>
                ))}
              </select>
              {taxCodesStatus === 'erro' && (
                <AvisoCampo $tone="erro">
                  Não foi possível carregar as tabelas tributárias. Não é possível salvar a natureza até carregar.
                  <AvisoCampoAcao type="button" onClick={loadTaxCodes}>Tentar de novo</AvisoCampoAcao>
                </AvisoCampo>
              )}
              {cstIpiGravadoInvalido && (
                <AvisoCampo>
                  CST {cstIPI} gravado não vale mais na natureza. Ao salvar, a natureza passa a seguir a regra do NCM.
                </AvisoCampo>
              )}
            </StyledFormGroup>
            {cstIpiNatureza && (
              <StyledFormGroup style={{ marginTop: '20px' }}>
                <LabelWithInfo label="Enquadramento (cEnq)" tooltip="Código de Enquadramento Legal do IPI, da tabela oficial, filtrado pelo CST." />
                <select value={codigoEnquadramentoIPI} onChange={(e) => setCodigoEnquadramentoIPI(e.target.value)}>
                  <option value="">
                    {cstIpiNatureza.enquadramentoPadrao ? `Padrão pelo CST (${cstIpiNatureza.enquadramentoPadrao})` : 'Selecione (obrigatório)'}
                  </option>
                  {enqOptions.map((o) => (
                    <option key={o.code} value={o.code} title={o.description}>
                      {o.code} — {o.description.length > 80 ? `${o.description.slice(0, 80)}…` : o.description}
                    </option>
                  ))}
                </select>
              </StyledFormGroup>
            )}
          </FormGrid>
        </StyledCard>

        {/* CENÁRIOS DINÂMICOS */}
        <div style={{ marginTop: '10px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#333', marginBottom: '4px' }}>Cenários fiscais</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Defina as regras de ICMS para cada agrupamento de estados</p>
        </div>

        {scenarios.map((scenario, index) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            index={index}
            updateScenario={updateScenario}
            removeScenario={(id: number) => setScenarios(scenarios.filter(s => s.id !== id))}
            allStates={allStates}
            taxCodes={taxCodes}
            classTribOptions={classTribOptions}
          />
        ))}

        <DashButton
          type="button"
          onClick={() => setScenarios([...scenarios, { id: Date.now(), ...DEFAULT_SCENARIO }])}
        >
          + Cenário
        </DashButton>
      </MainContainer>
    </>
  );
};

export default CreateTaxScenario;
